import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { CheckCircle, BookOpen, Languages, GraduationCap, Users, Award, Target } from 'lucide-react';
import classContentData from '../data/classContent.json';

// Icon mapping
const iconMap = {
  CheckCircle, BookOpen, Languages, GraduationCap, Users, Award, Target
};

const getIconComponent = (iconName, props = {}) => {
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent {...props} /> : <BookOpen {...props} />;
};

// Reusable EnrollmentCTA Component
const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

const EnrollmentCTA = ({ title, description, buttonText, linkTo }) => {
  const [isFloatingVisible, setFloatingVisible] = useState(false);
  const ctaRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (ctaRef.current) {
        const topPosition = ctaRef.current.getBoundingClientRect().top;
        if (topPosition < 0) {
          setFloatingVisible(true);
        } else {
          setFloatingVisible(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <div ref={ctaRef}>
        <section id="enroll" className="enrollment-cta-section">
          <div className="cta-content">
            <h3>{title}</h3>
            <p>{description}</p>
          </div>
          <div className="cta-action">
            <Link to={linkTo} className="cta-link-btn">
              {buttonText} <SendIcon />
            </Link>
          </div>
        </section>
      </div>

      <div className={`floating-cta ${isFloatingVisible ? 'visible' : ''}`}>
        <Link to={linkTo} className="cta-link-btn">
          {buttonText} <SendIcon />
        </Link>
      </div>
    </>
  );
};

const ClassTemplate = () => {
  const location = useLocation();

  // Extract classId from path (e.g., /dgca-classes -> dgca-classes)
  const classId = location.pathname.split('/').filter(Boolean)[0];

  // Get class data from JSON
  const classData = classContentData[classId];

  // If class not found, redirect to courses page
  if (!classData) {
    return <Navigate to="/courses" replace />;
  }

  const { hero, overview, subjects, approach, features, cta } = classData;

  return (
    <>
      <style>{`
        /* General Class Page Styles */
        .class-page { 
          font-family: 'Inter', sans-serif; 
          color: #333; 
        }
        
        .page-section { 
          max-width: 1100px; 
          margin: 80px auto; 
          padding: 0 20px; 
        }
        
        .section-title { 
          font-size: 2.8rem; 
          color: #001f3f; 
          text-align: center; 
          margin-bottom: 50px;
          font-weight: 800;
        }
        
        /* Hero Section */
        .hero {
          padding: 120px 20px; 
          text-align: center; 
          color: white;
          background-size: cover; 
          background-position: center;
          position: relative;
        }
        
        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(0, 31, 63, 0.85), rgba(0, 61, 130, 0.85));
          z-index: 1;
        }
        
        .hero > * {
          position: relative;
          z-index: 2;
        }
        
        .hero h1 { 
          font-size: 3.5rem; 
          font-weight: 800; 
          margin-bottom: 15px;
          line-height: 1.2;
        }
        
        .hero .subtitle {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 20px;
          color: #7dd3fc;
        }
        
        .hero p { 
          font-size: 1.25rem; 
          max-width: 700px; 
          margin: 0 auto 30px; 
          opacity: 0.95;
          line-height: 1.6;
        }
        
        .hero-cta-btn {
          background-color: #fff; 
          color: #001f3f; 
          padding: 16px 32px;
          text-decoration: none; 
          border-radius: 12px; 
          font-weight: bold;
          transition: all 0.3s ease;
          display: inline-block;
          font-size: 1.1rem;
        }
        
        .hero-cta-btn:hover { 
          background-color: #e6f7ff; 
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
        }

        /* Overview Section */
        .overview-grid { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 50px; 
          align-items: center; 
        }
        
        .overview-text h2 {
          font-size: 2.2rem;
          color: #001f3f;
          margin-bottom: 20px;
          font-weight: 800;
        }
        
        .overview-text p { 
          font-size: 1.15rem; 
          color: #555; 
          line-height: 1.8; 
        }
        
        .overview-image { 
          width: 100%; 
          border-radius: 16px;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        }

        /* Subjects Grid */
        .subjects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
          margin-top: 40px;
        }
        
        .subject-card {
          background: linear-gradient(135deg, #f8fafc, #ffffff);
          padding: 32px 24px;
          border-radius: 16px;
          text-align: center;
          border: 2px solid #e2e8f0;
          transition: all 0.3s ease;
        }
        
        .subject-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 32px rgba(0, 86, 179, 0.15);
          border-color: #0369a1;
        }
        
        .subject-icon {
          color: #0056b3;
          margin-bottom: 16px;
        }
        
        .subject-card h4 {
          font-size: 1.2rem;
          color: #001f3f;
          margin: 12px 0 8px;
          font-weight: 700;
        }
        
        .subject-card p {
          font-size: 0.95rem;
          color: #64748b;
          line-height: 1.6;
        }

        /* Feature List */
        .feature-list { 
          list-style: none; 
          padding: 0;
          display: grid;
          gap: 20px;
        }
        
        .feature-list li { 
          font-size: 1.1rem; 
          display: flex; 
          align-items: flex-start;
          background: linear-gradient(135deg, #f8fafc, #ffffff);
          padding: 20px;
          border-radius: 12px;
          border-left: 4px solid #0056b3;
        }
        
        .feature-list li svg {
          margin-right: 16px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        /* Features Grid */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 32px;
          margin-top: 40px;
        }
        
        .feature-card {
          background: white;
          padding: 32px;
          border-radius: 16px;
          border: 2px solid #e2e8f0;
          transition: all 0.3s ease;
        }
        
        .feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
          border-color: #0369a1;
        }
        
        .feature-icon-wrapper {
          width: 56px;
          height: 56px;
          background: linear-gradient(135deg, #e0f2fe, #dbeafe);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }
        
        .feature-icon {
          color: #0369a1;
        }
        
        .feature-card h3 {
          font-size: 1.3rem;
          color: #001f3f;
          margin-bottom: 12px;
          font-weight: 700;
        }
        
        .feature-card p {
          font-size: 1rem;
          color: #64748b;
          line-height: 1.6;
        }

        /* CTA Component Styles */
        .enrollment-cta-section {
          background: linear-gradient(135deg, #e0f2fe, #f0f9ff);
          padding: 70px 40px; 
          border-radius: 20px;
          max-width: 1100px; 
          margin: 80px auto; 
          display: grid;
          grid-template-columns: 2fr 1fr; 
          gap: 50px; 
          align-items: center;
          border: 2px solid #bae6fd;
          box-shadow: 0 8px 24px rgba(0, 86, 179, 0.1);
        }
        
        .cta-content h3 { 
          font-size: 2.5rem; 
          color: #001f3f; 
          margin-top: 0; 
          line-height: 1.2;
          font-weight: 800;
        }
        
        .cta-content p { 
          font-size: 1.15rem; 
          color: #334155;
          line-height: 1.7;
        }
        
        .cta-action { 
          display: flex; 
          justify-content: center; 
          align-items: center; 
        }
        
        .cta-link-btn {
          padding: 16px 32px; 
          background: linear-gradient(135deg, #0056b3, #003d82);
          color: white;
          border-radius: 12px; 
          font-size: 1.1rem; 
          font-weight: bold;
          transition: all 0.3s ease; 
          display: inline-flex;
          align-items: center; 
          gap: 10px; 
          text-decoration: none;
          box-shadow: 0 4px 16px rgba(0, 86, 179, 0.3);
        }
        
        .cta-link-btn:hover { 
          background: linear-gradient(135deg, #003d82, #002855);
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0, 86, 179, 0.4);
        }
        
        .floating-cta {
          position: fixed; 
          bottom: 30px; 
          right: 30px; 
          z-index: 1000;
          opacity: 0; 
          transform: translateY(20px);
          transition: opacity 0.4s ease, transform 0.4s ease;
          pointer-events: none;
        }
        
        .floating-cta.visible { 
          opacity: 1; 
          transform: translateY(0); 
          pointer-events: auto; 
        }

        /* Approach Section */
        .approach-content {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 40px;
        }
        
        .approach-content h3 {
          font-size: 1.8rem;
          color: #001f3f;
          margin-bottom: 16px;
          font-weight: 700;
        }
        
        .approach-content p {
          font-size: 1.1rem;
          color: #64748b;
          line-height: 1.7;
        }
        
        @media (max-width: 768px) {
          .overview-grid, 
          .enrollment-cta-section { 
            grid-template-columns: 1fr; 
            text-align: center;
            gap: 30px;
          }
          
          .hero h1 {
            font-size: 2.5rem;
          }
          
          .hero .subtitle {
            font-size: 1.2rem;
          }
          
          .section-title {
            font-size: 2.2rem;
          }
          
          .subjects-grid {
            grid-template-columns: 1fr;
          }
          
          .features-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="class-page">
        {/* Hero Section */}
        <section 
          className="hero" 
          style={{
            backgroundImage: hero.backgroundImage ? 
              `linear-gradient(rgba(0, 31, 63, 0.85), rgba(0, 61, 130, 0.85)), url('${hero.backgroundImage}')` : 
              'linear-gradient(135deg, #001f3f, #003d82)'
          }}
        >
          <h1>{hero.title}</h1>
          <div className="subtitle">{hero.subtitle}</div>
          <p>{hero.description}</p>
          <a href="#enroll" className="hero-cta-btn">{hero.ctaText}</a>
        </section>

        {/* Overview Section */}
        <section className="page-section">
          <div className="overview-grid">
            <div className="overview-text">
              <h2>{overview.title}</h2>
              <p>{overview.description}</p>
            </div>
            <img 
              src={overview.image} 
              alt={overview.title} 
              className="overview-image"
            />
          </div>
        </section>

        {/* Subjects Section */}
        <section className="page-section">
          <h2 className="section-title">{subjects.title}</h2>
          <div className="subjects-grid">
            {subjects.items.map((subject, index) => (
              <div key={index} className="subject-card">
                {getIconComponent(subject.icon, { size: 32, className: 'subject-icon' })}
                <h4>{subject.name}</h4>
                <p>{subject.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Approach Section */}
        <section className="page-section">
          <h2 className="section-title">{approach.title}</h2>
          <div className="approach-content">
            <h3>{approach.subtitle}</h3>
            <p>{approach.description}</p>
          </div>
          <ul className="feature-list">
            {approach.points.map((point, index) => (
              <li key={index}>
                <CheckCircle size={24} style={{ color: '#0056b3' }} />
                {point}
              </li>
            ))}
          </ul>
        </section>

        {/* Features Section */}
        {features && features.length > 0 && (
          <section className="page-section">
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon-wrapper">
                    {getIconComponent(feature.icon, { size: 28, className: 'feature-icon' })}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Enrollment CTA */}
        <EnrollmentCTA
          title={cta.title}
          description={cta.description}
          buttonText={cta.buttonText}
          linkTo={cta.linkTo}
        />
      </div>
    </>
  );
};

export default ClassTemplate;
